import React from 'react';

export default function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col-reverse md:flex-row items-center justify-center px-6 md:px-16 py-16 bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50 text-gray-900 overflow-hidden">
      
      {/* Animated Background Accents */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-sky-300 via-blue-300 to-cyan-300 blur-3xl opacity-40 animate-pulse"></div>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-br from-blue-400 via-sky-400 to-cyan-400 blur-3xl opacity-30 animate-pulse" style={{animationDelay: '1s'}}></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full bg-gradient-to-r from-cyan-200 to-blue-200 blur-3xl opacity-25 animate-pulse" style={{animationDelay: '2s'}}></div>

      {/* Left: Intro Text */}
      <div className="relative z-10 w-full md:w-1/2 text-center md:text-left">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 drop-shadow-lg">
          Your Journey Begins with{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-blue-600 to-cyan-500 animate-pulse">
            Travelrmania
          </span>
        </h1>
        
        <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
          <span className="inline-block px-4 py-1 rounded-full bg-sky-100 text-sky-700 font-semibold text-sm shadow-md border border-sky-300 hover:scale-105 transition-transform">
            Best Tour Agency in Delhi
          </span>
          <span className="inline-block px-4 py-1 rounded-full bg-cyan-100 text-cyan-700 font-semibold text-sm shadow-md border border-cyan-300 hover:scale-105 transition-transform">
            24/7 Available
          </span>
        </div>

        <p className="text-md sm:text-lg md:text-xl max-w-lg mx-auto md:mx-0 text-gray-600 leading-relaxed mb-6">
          Explore India's most breathtaking destinations with our luxury tempo travellers and expert travel services. From the Golden Triangle to Himalayan adventures — we create unforgettable experiences at the best prices.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mb-8">
          <a
            href="#packages"
            className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl hover:from-sky-600 hover:to-blue-700 transition-all duration-200"
          >
            View Packages
          </a>
          <a
            href="#contact"
            className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
          >
            Contact Us
          </a>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto md:mx-0">
          <div className="text-center md:text-left bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <p className="text-2xl font-bold text-sky-600">2</p>
            <p className="text-sm text-gray-600">Countries Covered</p>
          </div>
          <div className="text-center md:text-left bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <p className="text-2xl font-bold text-cyan-600">10%</p>
            <p className="text-sm text-gray-600">First Trip Discount</p>
          </div>
          <div className="text-center md:text-left bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <p className="text-2xl font-bold text-blue-600">24/7</p>
            <p className="text-sm text-gray-600">Support</p>
          </div>
        </div>
      </div>

      {/* Right: Featured Image with Gradient Ring */}
      <div className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 flex items-center justify-center mb-10 md:mb-0 md:ml-10 z-10">
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-sky-400 via-blue-500 to-cyan-400 p-1" style={{animation: 'spin 6s linear infinite'}}></div>
        <div className="relative w-full h-full rounded-full overflow-hidden bg-white shadow-2xl border-4 border-sky-100">
          <img
            src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=800&fit=crop"
            alt="Travelrmania"
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
          />
        </div>
        
        {/* Floating badges */}
        <div className="absolute -top-4 -right-4 bg-white px-4 py-2 rounded-full shadow-xl border-2 border-sky-400 hover:scale-110 transition-transform">
          <p className="text-xs font-bold text-sky-600">⭐ 5.0 Rating</p>
        </div>
        <div className="absolute -bottom-4 -left-4 bg-white px-4 py-2 rounded-full shadow-xl border-2 border-cyan-400 hover:scale-110 transition-transform">
          <p className="text-xs font-bold text-cyan-600">🚗 Sanitized Cars</p>
        </div>
      </div>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/918929550611"
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-5 py-4 rounded-full shadow-2xl flex items-center gap-2 hover:scale-110 transition-all duration-200 animate-bounce"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="font-semibold">Book Now</span>
      </a>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}


// import React from 'react';

// export default function Hero() {
//   return (
//     <section className="relative min-h-screen flex flex-col-reverse md:flex-row items-center justify-center px-6 md:px-16 py-16 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-white overflow-hidden">
      
//       {/* Animated Background Accents */}
//       <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-blue-600 via-blue-400 to-orange-400 blur-3xl opacity-20 animate-pulse"></div>
//       <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-br from-orange-500 via-blue-500 to-blue-300 blur-3xl opacity-15 animate-pulse" style={{animationDelay: '1s'}}></div>

//       {/* Left: Intro Text */}
//       <div className="relative z-10 w-full md:w-1/2 text-center md:text-left">
//         <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 drop-shadow-md">
//           Your Journey Begins with{' '}
//           <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-300 to-orange-400">
//             Travelrmania
//           </span>
//         </h1>
        
//         <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
//           <span className="inline-block px-4 py-1 rounded-full bg-blue-500/20 text-blue-300 font-semibold text-sm shadow border border-blue-400/30">
//             Best Tour Agency in Delhi
//           </span>
//           <span className="inline-block px-4 py-1 rounded-full bg-orange-500/20 text-orange-300 font-semibold text-sm shadow border border-orange-400/30">
//             24/7 Available
//           </span>
//         </div>

//         <p className="text-md sm:text-lg md:text-xl max-w-lg mx-auto md:mx-0 text-gray-300 leading-relaxed mb-6">
//           Explore India's most breathtaking destinations with our luxury tempo travellers and expert travel services. From the Golden Triangle to Himalayan adventures — we create unforgettable experiences at the best prices.
//         </p>

//         <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mb-8">
//           <a
//             href="#packages"
//             className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl transition-transform duration-200"
//           >
//             View Packages
//           </a>
//           <a
//             href="#contact"
//             className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl transition-transform duration-200"
//           >
//             Contact Us
//           </a>
//         </div>

//         {/* Quick Stats */}
//         <div className="grid grid-cols-3 gap-4 max-w-md mx-auto md:mx-0">
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-blue-400">2</p>
//             <p className="text-sm text-gray-400">Countries Covered</p>
//           </div>
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-orange-400">10%</p>
//             <p className="text-sm text-gray-400">First Trip Discount</p>
//           </div>
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-blue-300">24/7</p>
//             <p className="text-sm text-gray-400">Support</p>
//           </div>
//         </div>
//       </div>

//       {/* Right: Featured Image with Gradient Ring */}
//       <div className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 flex items-center justify-center mb-10 md:mb-0 md:ml-10 z-10">
//         <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 via-blue-400 to-orange-500 p-1" style={{animation: 'spin 6s linear infinite'}}></div>
//         <div className="relative w-full h-full rounded-full overflow-hidden bg-gray-800 shadow-xl border-4 border-gray-900">
//           <img
//             src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=800&fit=crop"
//             alt="Travelrmania"
//             className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
//           />
//         </div>
        
//         {/* Floating badges */}
//         <div className="absolute -top-4 -right-4 bg-gray-800 px-4 py-2 rounded-full shadow-lg border-2 border-blue-400">
//           <p className="text-xs font-bold text-blue-400">⭐ 5.0 Rating</p>
//         </div>
//         <div className="absolute -bottom-4 -left-4 bg-gray-800 px-4 py-2 rounded-full shadow-lg border-2 border-orange-400">
//           <p className="text-xs font-bold text-orange-400">🚗 Sanitized Cars</p>
//         </div>
//       </div>

//       {/* WhatsApp Button */}
//       <a
//         href="https://wa.me/918929550611"
//         className="fixed bottom-6 right-6 z-50 bg-orange-500 hover:bg-orange-600 text-white px-5 py-4 rounded-full shadow-lg flex items-center gap-2 hover:scale-110 transition-transform duration-200"
//         target="_blank"
//         rel="noopener noreferrer"
//       >
//         <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//           <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
//         </svg>
//         <span className="font-semibold">Book Now</span>
//       </a>

//       <style>{`
//         @keyframes spin {
//           from { transform: rotate(0deg); }
//           to { transform: rotate(360deg); }
//         }
//       `}</style>
//     </section>
//   );
// }

// import React from 'react';

// export default function Hero() {
//   return (
//     <section className="relative min-h-screen flex flex-col-reverse md:flex-row items-center justify-center px-6 md:px-16 py-16 bg-gradient-to-br from-blue-50 via-purple-100 to-white text-gray-800 overflow-hidden">
      
//       {/* Animated Background Accents */}
//       <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-blue-400 via-purple-300 to-pink-300 blur-3xl opacity-25 animate-pulse"></div>
//       <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-br from-pink-300 via-purple-200 to-blue-200 blur-3xl opacity-15 animate-pulse" style={{animationDelay: '1s'}}></div>

//       {/* Left: Intro Text */}
//       <div className="relative z-10 w-full md:w-1/2 text-center md:text-left">
//         <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 drop-shadow-md">
//           Your Journey Begins with{' '}
//           <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500">
//             Dream to Travels
//           </span>
//         </h1>
        
//         <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
//           <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 text-blue-700 font-semibold text-sm shadow">
//             Best Tour Agency in Delhi
//           </span>
//           <span className="inline-block px-4 py-1 rounded-full bg-gradient-to-r from-purple-100 via-pink-100 to-blue-100 text-purple-700 font-semibold text-sm shadow">
//             24/7 Available
//           </span>
//         </div>

//         <p className="text-md sm:text-lg md:text-xl max-w-lg mx-auto md:mx-0 text-gray-700 leading-relaxed mb-6">
//           Explore India's most breathtaking destinations with our luxury tempo travellers and expert travel services. From the Golden Triangle to Himalayan adventures — we create unforgettable experiences at the best prices.
//         </p>

//         <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mb-8">
//           <a
//             href="#packages"
//             className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl transition-transform duration-200"
//           >
//             View Packages
//           </a>
//           <a
//             href="#vehicles"
//             className="inline-block px-7 py-3 rounded-full bg-white text-purple-600 font-bold shadow-lg border-2 border-purple-500 hover:scale-105 hover:shadow-xl transition-transform duration-200"
//           >
//             Our Vehicles
//           </a>
//         </div>

//         {/* Quick Stats */}
//         <div className="grid grid-cols-3 gap-4 max-w-md mx-auto md:mx-0">
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-purple-600">102</p>
//             <p className="text-sm text-gray-600">Countries Covered</p>
//           </div>
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-blue-600">15%</p>
//             <p className="text-sm text-gray-600">First Trip Discount</p>
//           </div>
//           <div className="text-center md:text-left">
//             <p className="text-2xl font-bold text-pink-600">24/7</p>
//             <p className="text-sm text-gray-600">Support</p>
//           </div>
//         </div>
//       </div>

//       {/* Right: Featured Image with Gradient Ring */}
//       <div className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 flex items-center justify-center mb-10 md:mb-0 md:ml-10 z-10">
//         <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-400 via-purple-400 to-pink-400 p-1" style={{animation: 'spin 6s linear infinite'}}></div>
//         <div className="relative w-full h-full rounded-full overflow-hidden bg-white shadow-xl border-4 border-white">
//           <img
//             src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=800&fit=crop"
//             alt="Dream to Travels"
//             className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
//           />
//         </div>
        
//         {/* Floating badges */}
//         <div className="absolute -top-4 -right-4 bg-white px-4 py-2 rounded-full shadow-lg border-2 border-purple-200">
//           <p className="text-xs font-bold text-purple-600">⭐ 5.0 Rating</p>
//         </div>
//         <div className="absolute -bottom-4 -left-4 bg-white px-4 py-2 rounded-full shadow-lg border-2 border-blue-200">
//           <p className="text-xs font-bold text-blue-600">🚗 Sanitized Cars</p>
//         </div>
//       </div>

//       {/* WhatsApp Button */}
//       <a
//         href="https://wa.me/917027005176"
//         className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white px-5 py-4 rounded-full shadow-lg flex items-center gap-2 hover:scale-110 transition-transform duration-200"
//         target="_blank"
//         rel="noopener noreferrer"
//       >
//         <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//           <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
//         </svg>
//         <span className="font-semibold">Book Now</span>
//       </a>

//       <style>{`
//         @keyframes spin {
//           from { transform: rotate(0deg); }
//           to { transform: rotate(360deg); }
//         }
//       `}</style>
//     </section>
//   );
// }

// import React from 'react';
// import { motion } from 'framer-motion';
// import myPhoto from '../assets/jagdish.png';

// export default function Hero() {
//   return (
//     <section className="relative min-h-screen flex flex-col-reverse md:flex-row items-center justify-center px-6 md:px-16 py-16 bg-gradient-to-br from-blue-50 via-purple-100 to-white text-gray-800 overflow-hidden">
      
//       {/* Animated Background Accent */}
//       <motion.div
//         initial={{ opacity: 0, scale: 0.8 }}
//         animate={{ opacity: 0.25, scale: 1 }}
//         transition={{ duration: 1.2, delay: 0.2 }}
//         className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-tr from-blue-400 via-purple-300 to-pink-300 blur-3xl z-0"
//       />
//       <motion.div
//         initial={{ opacity: 0, scale: 0.8 }}
//         animate={{ opacity: 0.15, scale: 1 }}
//         transition={{ duration: 1.2, delay: 0.4 }}
//         className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-br from-pink-300 via-purple-200 to-blue-200 blur-3xl z-0"
//       />
// {/* Left: Intro Text */}
// <motion.div
//   initial={{ x: -60, opacity: 0 }}
//   animate={{ x: 0, opacity: 1 }}
//   transition={{ duration: 0.7 }}
//   className="relative z-10 w-full md:w-1/2 text-center md:text-left"
// >
//   <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 drop-shadow-[2px_2px_3px_rgba(0,0,0,0.08)]">
//     Hi, I'm{' '}
//     <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 drop-shadow-[3px_3px_4px_rgba(0,0,0,0.12)]">
//       Jagdish Singh
//     </span>
//   </h1>
//   <span className="inline-block px-4 py-1 mb-4 rounded-full bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 text-blue-700 font-semibold text-sm shadow">
//     Full Stack Developer
//   </span>
//   <p className="text-md sm:text-lg md:text-xl max-w-lg mx-auto md:mx-0 text-gray-700 leading-relaxed mb-6">
//     I design and build seamless digital experiences, from interactive user interfaces to robust backend solutions — all tailored to enhance performance, usability, and impact.
//   </p>
//   <a
//     href="#contact"
//     className="inline-block px-7 py-3 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 text-white font-bold shadow-lg hover:scale-105 hover:shadow-xl transition-transform duration-200"
//   >
//     Let's Connect
//   </a>
// </motion.div>


//       {/* Right: Profile Image with Gradient Ring */}
//       <motion.div
//         initial={{ scale: 0.9, opacity: 0 }}
//         animate={{ scale: 1, opacity: 1 }}
//         transition={{ duration: 0.9 }}
//         className="relative w-48 h-48 sm:w-60 sm:h-60 md:w-72 md:h-72 flex items-center justify-center mb-10 md:mb-0 md:ml-10 z-10"
//       >
//         <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-400 via-purple-400 to-pink-400 p-1 animate-spin-slow" style={{animationDuration: '6s'}}></div>
//         <div className="relative w-full h-full rounded-full overflow-hidden bg-white shadow-xl border-4 border-white">
//           <img
//             src={myPhoto}
//             alt="Jagdish Singh"
//             className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
//           />
//         </div>
//       </motion.div>
//                     <a
//   href="https://wa.me/9755856913"
//   className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-lg"
//   target="_blank"
//   rel="noopener noreferrer"
// >
//   Chat on WhatsApp
// </a>
//     </section>
//   );
// }
